import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as BS from 'react-bootstrap';
import * as BSEx from '../../BootstrapExtends';
import Panel from '../../../models/SpreadsheetReportConfigs/Panel.js';
import {
  ExportTypeOptions,
  ShowHeaderRowOptions,
} from '../../../enums/SpreadsheetReportConfigs';
import {
  PERIOD_OPTION_VALUES,
  OVER_PERIOD_THRESHOLD_DAYS_VALUES,
  OVER_PERIOD_THRESHOLD_DAYS_DISABLED_AGGREGATION_AXIS_COLUMNS,
  EXPORT_TYPE_OPTION_VALUES,
  DEFAULT_PANEL_VALUE,
  PERIOD_THRESHOLD_DAYS,
} from '../../../constants/SpreadsheetReportConfigs';
import { getDaysBetweenDates } from '../../../lib/DateUtils.js';
import CustomReportTablePanel from './CustomReportTablePanel.jsx';
import CustomReportStringPanel from './CustomReportStringPanel.jsx';
import SpreadsheetReportConfigs from '../../../models/SpreadsheetReportConfigs/index.js';
import ConversionPoint from '../../../models/SpreadsheetReportConfigs/ConversionPoint';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Form.CustomReportForm';

const propTypes = {
  panel: PropTypes.exact(Panel.propTypes),
  updatedSpreadsheetReportConfigs: PropTypes.shape(
    SpreadsheetReportConfigs.propTypes
  ),
  errors: PropTypes.object.isRequired,
  touched: PropTypes.instanceOf(Object),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  conversionPoints: PropTypes.arrayOf(
    PropTypes.shape(ConversionPoint.propTypes)
  ),
  channelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

const CustomReportPanel = ({
  panel,
  updatedSpreadsheetReportConfigs,
  errors = {},
  touched = {},
  onChange,
  onBlur,
  conversionPoints,
  channelOptions = [],
}) => {
  const isOverPeriodThreshold = useMemo(() => {
    if (
      panel.period === PERIOD_OPTION_VALUES.custom_period &&
      panel.startedOn &&
      panel.endedOn
    ) {
      return (
        getDaysBetweenDates(panel.startedOn, panel.endedOn) >=
        PERIOD_THRESHOLD_DAYS
      );
    }
    return OVER_PERIOD_THRESHOLD_DAYS_VALUES.includes(panel.period);
  }, [panel.period, panel.startedOn, panel.endedOn]);

  const [fieldFocusState, setFieldFocusState] = useState({
    column: Boolean(panel.column),
    row: Boolean(panel.row),
    numberOfMaxLines: Boolean(panel.numberOfMaxLines),
  });

  const [validationError, setValidationError] = useState(null);

  const hasRestrictedColumns = useMemo(() => {
    return panel.columns.some((column) =>
      OVER_PERIOD_THRESHOLD_DAYS_DISABLED_AGGREGATION_AXIS_COLUMNS.includes(
        column.content
      )
    );
  }, [panel.columns]);

  const restrictedColumnContents = useMemo(() => {
    return panel.columns
      .filter((column) =>
        OVER_PERIOD_THRESHOLD_DAYS_DISABLED_AGGREGATION_AXIS_COLUMNS.includes(
          column.content
        )
      )
      .map((column) => column.content);
  }, [panel.columns]);

  const _shouldHighlightRestrictedColumns =
    isOverPeriodThreshold && restrictedColumnContents.length > 0;

  useEffect(() => {
    setFieldFocusState({
      column: Boolean(panel.column),
      row: Boolean(panel.row),
      numberOfMaxLines: Boolean(panel.numberOfMaxLines),
    });
  }, [panel.id]);

  const handleInitialFieldFocus = (fieldName) => () => {
    setFieldFocusState((prev) => {
      if (prev[fieldName]) {
        return prev;
      }
      return {
        ...prev,
        [fieldName]: true,
      };
    });
  };

  const willExceedThreshold = (periodValue, startedOnValue, endedOnValue) => {
    if (periodValue === PERIOD_OPTION_VALUES.custom_period) {
      if (!startedOnValue || !endedOnValue) return false;
      return (
        getDaysBetweenDates(startedOnValue, endedOnValue) >=
        PERIOD_THRESHOLD_DAYS
      );
    }
    return OVER_PERIOD_THRESHOLD_DAYS_VALUES.includes(periodValue);
  };

  const getPeriodErrorMessage = () =>
    I18n.t(`${scope}.messages.periodHasRestrictedColumns`, {
      thresholdDays: PERIOD_THRESHOLD_DAYS,
    });

  const shouldBlockPeriodChange = (
    periodValue,
    startedOnValue,
    endedOnValue
  ) => {
    if (!hasRestrictedColumns) return false;
    return willExceedThreshold(periodValue, startedOnValue, endedOnValue);
  };

  const handlePeriodChange = (value) => {
    // 「日付を指定」(custom_period) への切替時は、preset 期間時代に残っていた
    // startedOn/endedOn を持ち越さずにリセットする。古い日付 (93 日以上) を
    // 引きずって切替自体がブロックされ、カスタム期間に入れなくなるのを防ぐ。
    if (value === PERIOD_OPTION_VALUES.custom_period) {
      setValidationError(null);
      onChange('period', value);
      onChange('startedOn', null);
      onChange('endedOn', null);
      return;
    }

    if (shouldBlockPeriodChange(value, panel.startedOn, panel.endedOn)) {
      const message = getPeriodErrorMessage();
      setValidationError({ field: 'period', message });
      return;
    }
    setValidationError(null);
    onChange('period', value);
  };

  const handleEventChange = (fieldName) => (event) => {
    let value = event.target.value;
    if (event.target.type === 'number') {
      value = value === '' ? '' : Number(value);
    }
    if (fieldName === 'period') {
      handlePeriodChange(value);
      return;
    }
    onChange(fieldName, value);
  };

  const handleValueChange = (fieldName) => (value) => {
    if (fieldName === 'startedOn' || fieldName === 'endedOn') {
      const nextStartedOn = fieldName === 'startedOn' ? value : panel.startedOn;
      const nextEndedOn = fieldName === 'endedOn' ? value : panel.endedOn;
      if (shouldBlockPeriodChange(panel.period, nextStartedOn, nextEndedOn)) {
        const message = getPeriodErrorMessage();
        setValidationError({ field: fieldName, message });
        return;
      }
      setValidationError(null);
    }
    onChange(fieldName, value);
  };

  const handlePanelTypeChange = (event) => {
    const value = event.target.value;
    if (value === EXPORT_TYPE_OPTION_VALUES.table) {
      onChange('content', DEFAULT_PANEL_VALUE.content);
      onChange('period', updatedSpreadsheetReportConfigs.period);
      onChange('startedOn', updatedSpreadsheetReportConfigs.startDate);
      onChange('endedOn', updatedSpreadsheetReportConfigs.endDate);
    } else {
      onChange('showHeaderRow', DEFAULT_PANEL_VALUE.showHeaderRow);
      onChange('numberOfMaxLines', DEFAULT_PANEL_VALUE.numberOfMaxLines);
      onChange('startedOn', DEFAULT_PANEL_VALUE.startedOn);
      onChange('endedOn', DEFAULT_PANEL_VALUE.endedOn);
      onChange('columns', DEFAULT_PANEL_VALUE.columns);
      onChange('filters', []);
      onChange('sorts', DEFAULT_PANEL_VALUE.sorts);
      onChange('period', '');
    }
    handleValueChange('type')(value);
  };

  const handleColumnChange = (e) => {
    const value = e.target.value;
    if (value !== value.toUpperCase()) {
      e.target.value = value.toUpperCase();
    }
    handleEventChange('column')(e);
  };

  useEffect(() => {
    if (!hasRestrictedColumns) {
      setValidationError(null);
    }
  }, [hasRestrictedColumns]);

  const enhancedErrors = validationError
    ? {
        ...errors,
        [validationError.field]:
          errors[validationError.field] || validationError.message,
      }
    : errors;
  const enhancedTouched = validationError
    ? { ...touched, [validationError.field]: true }
    : touched;

  return (
    <>
      {/* Panel Name */}
      <BSEx.FormGroupInline
        label={I18n.t(
          `${scope}.sheetConfigSection.panelSection.field.name.label`
        )}
        error={errors.name}
        touched={touched.name}
      >
        <BS.FormControl
          placeholder={I18n.t(
            `${scope}.sheetConfigSection.panelSection.field.name.placeholder`
          )}
          type='text'
          name='name'
          value={panel.name}
          onChange={handleEventChange('name')}
          onBlur={() => onBlur('name')}
        />
      </BSEx.FormGroupInline>

      {/* Panel Type */}
      <BSEx.FormGroupInline
        label={I18n.t(
          `${scope}.sheetConfigSection.panelSection.field.type.label`
        )}
        error={errors.type}
        touched={touched.type}
      >
        {Object.entries(ExportTypeOptions).map(([key, option]) => (
          <BS.Radio
            key={key}
            name={`panel-${panel.id}-type`}
            value={option.value}
            checked={panel.type === option.value}
            onChange={handlePanelTypeChange}
            onBlur={() => onBlur('type')}
            inline
          >
            {option.label}
          </BS.Radio>
        ))}
      </BSEx.FormGroupInline>

      {panel.type === EXPORT_TYPE_OPTION_VALUES.table && (
        <BSEx.FormGroupInline
          label={I18n.t(
            `${scope}.sheetConfigSection.panelSection.field.showHeaderRow.label`
          )}
          error={errors.showHeaderRow}
          touched={touched.showHeaderRow}
        >
          {Object.entries(ShowHeaderRowOptions).map(([key, option]) => (
            <BS.Radio
              key={key}
              name={`panel-${panel.id}-showHeaderRow`}
              value={option.value}
              checked={panel.showHeaderRow === option.value}
              onChange={(e) =>
                onChange('showHeaderRow', e.target.value === 'true')
              }
              onBlur={() => onBlur('showHeaderRow')}
              inline
            >
              {option.label}
            </BS.Radio>
          ))}
        </BSEx.FormGroupInline>
      )}

      <BS.FormGroup style={{ marginBottom: '0px' }}>
        <BS.Col sm={6}>
          {/* Column */}
          <BSEx.FormGroupInline
            className={
              fieldFocusState.column
                ? undefined
                : 'custom-report-form-field-unfocused'
            }
            label={I18n.t(
              `${scope}.sheetConfigSection.panelSection.field.column.label`
            )}
            labelColProps={{ sm: 4 }}
            childrenColProps={{ sm: 6 }}
            error={errors.column}
            touched={touched.column}
          >
            <BS.FormControl
              placeholder={I18n.t(
                `${scope}.sheetConfigSection.panelSection.field.column.placeholder`
              )}
              type='text'
              name='column'
              value={panel.column}
              onChange={handleColumnChange}
              onBlur={() => onBlur('column')}
              onFocus={handleInitialFieldFocus('column')}
            />
          </BSEx.FormGroupInline>
        </BS.Col>

        <BS.Col sm={6}>
          {/* Row */}
          <BSEx.FormGroupInline
            className={
              fieldFocusState.row
                ? undefined
                : 'custom-report-form-field-unfocused'
            }
            label={I18n.t(
              `${scope}.sheetConfigSection.panelSection.field.row.label`
            )}
            labelColProps={{ sm: 3 }}
            childrenColProps={{ sm: 6 }}
            error={errors.row}
            touched={touched.row}
          >
            <BS.FormControl
              placeholder={I18n.t(
                `${scope}.sheetConfigSection.panelSection.field.row.placeholder`
              )}
              type='number'
              name='row'
              value={panel.row}
              onChange={handleEventChange('row')}
              onBlur={() => onBlur('row')}
              onFocus={handleInitialFieldFocus('row')}
            />
          </BSEx.FormGroupInline>
        </BS.Col>
      </BS.FormGroup>

      {panel.type === EXPORT_TYPE_OPTION_VALUES.table ? (
        <CustomReportTablePanel
          panel={panel}
          isOverPeriodThreshold={isOverPeriodThreshold}
          errors={enhancedErrors}
          touched={enhancedTouched}
          onEventChange={handleEventChange}
          onValueChange={handleValueChange}
          onBlur={onBlur}
          conversionPoints={conversionPoints}
          channelOptions={channelOptions}
          shouldHighlightNumberOfMaxLines={!fieldFocusState.numberOfMaxLines}
          onInitialFieldFocus={handleInitialFieldFocus}
        />
      ) : (
        <CustomReportStringPanel
          updatedSpreadsheetReportConfigs={updatedSpreadsheetReportConfigs}
          panel={panel}
          errors={enhancedErrors}
          touched={enhancedTouched}
          onEventChange={handleEventChange}
          onValueChange={handleValueChange}
          onBlur={onBlur}
        />
      )}
    </>
  );
};

CustomReportPanel.propTypes = propTypes;

export default CustomReportPanel;
